import {useCallback, useState} from 'react';
import {
    View,
    Text,
    StyleSheet,
    ScrollView,
    RefreshControl,
} from 'react-native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import {useCurrentTheme} from "@/theming/useCurrentTheme";
import {useOrganizationAchievements} from "@/data/app-data-hooks";
import {HeaderText} from "@/components/common/HeaderText";
import {ThemeType} from "@/theming/baseTheme";
import {appDataKeys} from "@/data/app-data-keys";
import {useOrganizationNavigation} from "@/hooks/useOrganizationNavigation";
import {useHardwareBack} from "@/hooks/useHardwareBack";
import {AchievementContent} from "@/components/achievement/AchievementContent";
import {BaseCard} from "@/components/common/BaseCard";
import {Entypo} from "@expo/vector-icons";

interface AchievementsScreenProps {
    orgId: string;
}

export const AchievementsScreen = ({orgId}: AchievementsScreenProps) => {
    const {t} = useTranslation();
    const {theme} = useCurrentTheme();
    const {data: achievements, isLoading} = useOrganizationAchievements(orgId);
    const queryClient = useQueryClient();
    const [isRefreshing, setIsRefreshing] = useState(false);
    const insets = useSafeAreaInsets();
    const navigation = useOrganizationNavigation(orgId);

    const styles = createStyles(theme, insets);

    // Sort achievements by assigned date (most recent first)
    const sortedAchievements = [...achievements].sort((a, b) =>
        new Date(b.assigned || 0).getTime() - new Date(a.assigned || 0).getTime()
    );

    const handleBack = useCallback(() => {
        navigation.navigateBack();
    }, [navigation]);

    const handleRefresh = useCallback(async () => {
        setIsRefreshing(true);
        try {
            await queryClient.invalidateQueries({queryKey: appDataKeys.all});
        } finally {
            setIsRefreshing(false);
        }
    }, [queryClient]);

    // Handle hardware back button
    useHardwareBack(() => {
        handleBack();
        return true;
    });

    const renderHeader = () => (
        <View>
            <HeaderText
                title={t('achievements.title')}
                onBackPress={handleBack}
            />
            <View style={styles.subHeader}>
                <Text style={styles.subtitle}>{t('achievements.subtitle')}</Text>
                <View style={styles.countBadge}>
                    <Entypo name={"price-ribbon"} style={styles.countIcon}/>
                    <Text style={styles.countText}>
                        {t('achievements.count', {count: achievements.length})}
                    </Text>
                </View>
            </View>
        </View>
    );

    const renderEmptyState = () => (
        <View style={styles.emptyState}>
            <Text style={styles.emptyIcon}>🎯</Text>
            <Text style={styles.emptyTitle}>{t('achievements.emptyState.title')}</Text>
            <Text style={styles.emptyDescription}>
                {t('achievements.emptyState.description')}
            </Text>
        </View>
    );

    return (
        <View style={styles.container}>
            {renderHeader()}
            <ScrollView
                style={styles.scrollView}
                contentContainerStyle={styles.scrollContent}
                refreshControl={
                    <RefreshControl
                        refreshing={isRefreshing}
                        onRefresh={handleRefresh}
                        colors={[theme.colors.primary]}
                        tintColor={theme.colors.primary}
                    />
                }
            >
                {sortedAchievements.length === 0 && !isLoading ? (
                    renderEmptyState()
                ) : (
                    sortedAchievements.map(achievement => (
                        <BaseCard variant={"large"} key={achievement.id}>
                            <AchievementContent achievement={achievement} size={'md'}/>
                        </BaseCard>
                    ))
                )}
            </ScrollView>
        </View>
    );
};

const createStyles = (theme: ThemeType, insets: any) => StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: theme.colors.background,
    },

    subHeader: {
        backgroundColor: theme.colors.primary,
        paddingVertical: theme.spacing.md
    },

    subtitle: {
        fontSize: theme.fontSize.md,
        color: 'rgba(255, 255, 255, 0.9)',
        textAlign: 'center',
        marginTop: theme.spacing.xs,
        marginBottom: theme.spacing.lg,
    },

    countBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        borderRadius: 999,
        paddingVertical: theme.spacing.sm,
        paddingHorizontal: theme.spacing.lg,
        marginHorizontal: theme.spacing.xl,
    },

    countIcon: {
        fontSize: theme.fontSize.lg,
        color: theme.colors.text,
        marginRight: theme.spacing.xs,
    },

    countText: {
        fontSize: theme.fontSize.sm,
        fontWeight: '600',
        color: 'white',
    },

    scrollView: {
        flex: 1,
    },

    scrollContent: {
        paddingTop: theme.spacing.lg,
        paddingBottom: theme.spacing.xl,
    },

    emptyState: {
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: theme.spacing.xl,
        paddingVertical: theme.spacing.xl * 2,
    },

    emptyIcon: {
        fontSize: 64,
        marginBottom: theme.spacing.lg,
    },

    emptyTitle: {
        fontSize: theme.fontSize.xl,
        fontWeight: '600',
        color: theme.colors.text,
        textAlign: 'center',
        marginBottom: theme.spacing.sm,
    },

    emptyDescription: {
        fontSize: theme.fontSize.md,
        color: theme.colors.textSecondary,
        textAlign: 'center',
        lineHeight: theme.fontSize.md * 1.4,
    },
});
